/**
 * 작성자 : 홍선영
 * 날짜 : 2026.06.18
 * 기능 : 센서 종합현황 위젯(dCd 152) - 테이블 뷰 (대안 ① 2단 그룹 헤더)
 *
 * 한 페이지(설치 위치 최대 5개)만 렌더한다. 페이징/자동전환은 상위 위젯이 담당.
 */
import { useTranslation } from 'react-i18next';
import { SensorGroup, SensorLocation } from './types';
import { DEFAULT_SUB_LABEL, flattenUnits, readingDisplay, statusClass } from './helpers';
import LocationLabel from './LocationLabel';
import { SensorTable } from './styles';

interface Props {
  groups: SensorGroup[];
  locations: SensorLocation[]; // 이미 현재 페이지로 잘려진 목록
  fontPx?: number; // 셀 크기에 맞춰 상위에서 계산한 폰트 크기(px). 미지정 시 CSS 기본값
}

const SensorTableView = ({ groups, locations, fontPx }: Props) => {
  const { t } = useTranslation();
  const units = flattenUnits(groups);
  const dataColCount = units.length || 1;
  // 2단 헤더 여부: '진짜' 다중 컬럼 그룹(PM 등)만 해당. inline(방식 B) 그룹은 단일 헤더라 제외.
  const hasGroupedHeader = groups.some((g) => !g.inline && Array.isArray(g.units) && g.units.length > 1);
  const headRowSpan = hasGroupedHeader ? 2 : 1;

  return (
    <SensorTable style={fontPx ? { fontSize: `${fontPx}px` } : undefined}>
      <colgroup>
        {/* 설치 위치 라벨 컬럼 + 데이터 컬럼 균등 분할 */}
        <col style={{ width: '16%' }} />
        {units.map((u) => (
          <col key={u.key} style={{ width: `${84 / dataColCount}%` }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th rowSpan={headRowSpan}>{t('설치 위치')}</th>
          {groups.map((group) => {
            const span = Array.isArray(group.units) ? group.units.length : 0;
            if (span <= 0) return null;
            // 단일 센서 그룹 : 한 컬럼이 2줄을 차지(rowspan), 단위는 보조 라벨로
            if (span === 1) {
              const only = group.units[0];
              return (
                <th key={group.key} rowSpan={headRowSpan}>
                  {group.label}
                  {only.unitText && <span className='sub'>({only.unitText})</span>}
                </th>
              );
            }
            // 다중 세부위치(inline) : 단일 헤더가 colSpan 으로 여러 칸을 차지(sub-헤더 없음)
            if (group.inline) {
              const groupUnit = group.units[0]?.unitText;
              return (
                <th key={group.key} className='group-head' colSpan={span} rowSpan={headRowSpan}>
                  {group.label}
                  {groupUnit && <span className='sub'>({groupUnit})</span>}
                </th>
              );
            }
            // 동일 센서 다중 : 상단 대분류 그룹 헤더
            return (
              <th key={group.key} className='group-head' colSpan={span}>
                {group.label}
              </th>
            );
          })}
        </tr>
        {hasGroupedHeader && (
          <tr>
            {groups.map((group) => {
              const span = Array.isArray(group.units) ? group.units.length : 0;
              if (span <= 1 || group.inline) return null; // 단일/inline(방식 B) 그룹은 위에서 처리됨
              return group.units.map((u) => (
                <th key={u.key}>
                  {u.label}
                  {u.unitText && <span className='sub'>({u.unitText})</span>}
                </th>
              ));
            })}
          </tr>
        )}
      </thead>
      <tbody>
        {locations.map((loc) => (
          <tr key={loc.key}>
            <td className='loc-label'>
              <LocationLabel text={loc.name} fontPx={fontPx} />
            </td>
            {groups.map((group) => {
              const span = Array.isArray(group.units) ? group.units.length : 0;
              if (span <= 0) return null;
              // inline(다중 세부위치) 그룹: 그룹 전체를 한 칸(colSpan=span)으로 두고,
              // 그 안에서 이 위치가 실제 가진 측정점 개수만큼 flex 로 '정확히' 균등 분할한다.
              // (정수 colSpan 분배와 달리 나눠떨어지지 않아도 칸 너비가 완전히 같다)
              //  - 측정점 0개 → '-' 한 칸
              //  - 측정점 k개 → k등분
              if (group.inline) {
                const filled = group.units.filter((u) => loc.readings && loc.readings[u.key] !== undefined);
                const cells = filled.length > 0 ? filled : [null];
                return (
                  <td key={group.key} className='inline-merge' colSpan={span}>
                    <div className='inline-cells'>
                      {cells.map((u, idx) => {
                        const reading = u && loc.readings ? loc.readings[u.key] : undefined;
                        return (
                          <div key={u ? u.key : idx} className='inline-cell'>
                            {/* 실제 세부위치 라벨(옥상 등)은 유지, 기본 placeholder는 숨김 */}
                            {reading?.label && reading.label !== DEFAULT_SUB_LABEL && <span className='loc-tag'>{reading.label}</span>}
                            <span className={`val ${statusClass(reading?.status)}`}>{readingDisplay(reading)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                );
              }
              // 비 inline(단일 컬럼) 그룹: 각 측정점을 개별 셀로
              return group.units.map((u) => {
                const reading = loc.readings ? loc.readings[u.key] : undefined;
                return (
                  <td key={u.key}>
                    {reading?.label && <span className='loc-tag'>{reading.label}</span>}
                    <span className={`val ${statusClass(reading?.status)}`}>{readingDisplay(reading)}</span>
                  </td>
                );
              });
            })}
          </tr>
        ))}
      </tbody>
    </SensorTable>
  );
};

export default SensorTableView;
